import { useState, type FormEvent } from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  TextField,
} from '@mui/material';
import type { SettingsProjectRow } from '../../types';
import { PROJECT_MODAL_LABELS as LABELS  } from '../../../../utils/labels';
import AppDialog from '../../../../components/AppDialog';
import { useModalHistory } from '../../../../utils/useModalHistory';
import SpinnerButton from '../../../../components/SpinnerButton';

interface ProjectModalProps {
  project?: SettingsProjectRow;
  onClose: () => void;
  onSave: (name: string, year: number) => Promise<boolean>;
}

export default function ProjectModal({ project, onClose, onSave }: ProjectModalProps) {
  useModalHistory(onClose);
  const [name, setName] = useState(project?.name ?? '');
  const [year, setYear] = useState(project?.year ?? new Date().getFullYear());
  const [nameError, setNameError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setNameError(LABELS.nameRequired);
      return;
    }
    setSaving(true);
    const ok = await onSave(name.trim(), year);
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <AppDialog open onClose={onClose} maxWidth="xs" fullWidth title={project ? LABELS.editTitle : LABELS.createTitle} confirmLabel={LABELS.save} confirmFormId="project-form" confirmDisabled={saving} confirmLoading={saving} testId="project-modal">
      <form id="project-form" onSubmit={handleSubmit} noValidate>

        <DialogContent className="flex flex-col gap-4">
          <TextField
            label={LABELS.nameLabel}
            value={name}
            onChange={(e) => { setName(e.target.value); if (nameError) setNameError(''); }}
            required
            autoFocus
            fullWidth
            className="mt-2"
            error={!!nameError}
            helperText={nameError || undefined}
          />
          <TextField
            label={LABELS.yearLabel}
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            required
            fullWidth
            inputProps={{ min: 2000, max: 2100 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" data-testid="cancel-button">{LABELS.cancel}</Button>
          <SpinnerButton type="submit" variant="contained" loading={saving} data-testid="save-button">
            {LABELS.save}
          </SpinnerButton>
        </DialogActions>
      </form>
    </AppDialog>
  );
}
